// ============================================================================
// Échantillons → environnements (`samples` → `envs`).
// ============================================================================
// Pour juger une égalité « pour tout x » (identité, récurrence), le moteur
// l'évalue sur quelques environnements. Les écrire à la main oblige à calculer
// chaque valeur (A pour x = −3, −2…) : source d'erreurs pour un générateur.
// Le modèle peut donc donner seulement les valeurs des variables libres et les
// définitions ; le moteur calcule le reste.
// ============================================================================

import type { ScratchModel, SamplesSpec } from './model';
import { ev, parseText, type Env } from './tree';

/** Nombre maximal d'environnements (au-delà, le jugement devient lent). */
export const MAX_ENVS = 16;

export class SamplesError extends Error {}

/** Développe des échantillons en environnements. Lance `SamplesError` si une définition est mal formée. */
export function expandSamples(spec: SamplesSpec): Env[] {
  let envs: Env[] = [{}];
  Object.keys(spec.vars).forEach((name) => {
    const values = spec.vars[name]!;
    envs = envs.flatMap((e) => values.map((v) => ({ ...e, [name]: v })));
  });
  // produit cartésien trop grand : on garde des environnements répartis régulièrement
  if (envs.length > MAX_ENVS) {
    const step = envs.length / MAX_ENVS;
    envs = Array.from({ length: MAX_ENVS }, (_, i) => envs[Math.floor(i * step)]!);
  }
  (spec.defs || []).forEach((def) => {
    let line;
    try {
      line = parseText(def);
    } catch {
      throw new SamplesError(`définition illisible : « ${def} »`);
    }
    const d = line.dis.length === 1 ? line.dis[0]! : null;
    const target = d && d.sides.length === 2 && d.sides[0]!.k === 's' ? d.sides[0]!.n! : null;
    if (!d || !target)
      throw new SamplesError(`une définition s'écrit « grandeur = expression » : « ${def} »`);
    envs.forEach((env) => {
      const v = ev(d.sides[1]!, env);
      if (!Number.isFinite(v))
        throw new SamplesError(
          `« ${def} » ne se calcule pas pour ${Object.entries(env)
            .map(([k, x]) => `${k} = ${x}`)
            .join(', ')}`,
        );
      env[target] = v;
    });
  });
  return envs;
}

/**
 * Le modèle prêt à jouer : `envs` calculés depuis `samples` s'il n'en a pas.
 * Renvoie le même objet quand il n'y a rien à faire.
 */
/** Valeur d'une expression exacte (« ln(5) ÷ 2 ») ; lance `SamplesError` si elle ne se calcule pas. */
export function exactNumber(expr: string): number {
  let v = NaN;
  try {
    const line = parseText(expr);
    const d = line.dis.length === 1 ? line.dis[0]! : null;
    if (d && d.sides.length === 1) v = ev(d.sides[0]!, {});
  } catch {
    v = NaN;
  }
  if (!Number.isFinite(v)) throw new SamplesError(`valeur exacte illisible : « ${expr} »`);
  return v;
}

/** Le modèle a-t-il des valeurs exactes à calculer (moteur v2) ? */
export function hasExactValues(model: ScratchModel): boolean {
  return (
    Object.values(model.symbols).some((s) => !!s.exact?.length) ||
    (model.choices ?? []).some((c) => !!c.exact && c.value === undefined)
  );
}

/** `exact` → `value` / `values` (grandeurs) et `value` (choix). */
function resolveExact(model: ScratchModel): ScratchModel {
  if (!hasExactValues(model)) return model;
  const symbols: ScratchModel['symbols'] = {};
  Object.entries(model.symbols).forEach(([n, s]) => {
    if (!s.exact?.length) {
      symbols[n] = s;
      return;
    }
    const vals = s.exact.map(exactNumber);
    symbols[n] = s.given ? { ...s, value: vals[0] } : { ...s, values: vals };
  });
  // nombres complexes (moteur v11) : un choix « 16i » garde sa forme exacte (pas de valeur réelle)
  const choices = model.choices?.map((c) =>
    c.exact && c.value === undefined && !model.complex ? { ...c, value: exactNumber(c.exact) } : c,
  );
  return { ...model, symbols, ...(choices ? { choices } : {}) };
}

export function materializeModel(input: ScratchModel): ScratchModel {
  const model = resolveExact(input);
  if (!model.samples || model.envs) return model;
  const envs = expandSamples(model.samples);
  if (envs.length < 2) throw new SamplesError('il faut au moins 2 environnements d’échantillons');
  return { ...model, envs };
}
